/**
 * LES DATES D'ACTES, LUES SANS SE TROMPER DE JOUR.
 *
 * A Postgres `date` does not always reach the browser as "2024-05-01". Through
 * the driver it can arrive as a full timestamp, and sometimes as a Date that
 * has already been through JSON. The old helper glued "T00:00:00" onto
 * whatever it got, so a timestamp became "2024-05-01T00:00:00.000ZT00:00:00"
 * — which is how a table full of real dates ended up printing "Invalid Date".
 *
 * So: take the first ten characters when they look like a day, fall back to
 * letting the browser parse the rest, and NEVER shift the day. A certificate
 * issued on the 1st must not read as the 30th because of a timezone.
 */
const DAY = /^(\d{4})-(\d{2})-(\d{2})/;

/** The plain YYYY-MM-DD behind whatever the server sent, or null. */
export function acteDay(v: unknown): string | null {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString().slice(0, 10);
  const s = String(v).trim();
  if (!s) return null;
  const m = DAY.exec(s);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

/** That day, written for a person. Empty dates read as an em dash, never as
 *  "Invalid Date" — a broken date must not look like a real one. */
export function fmtDay(v: unknown, lang: string, opts?: Intl.DateTimeFormatOptions): string {
  const day = acteDay(v);
  if (!day) return '—';
  /* Built from the parts, so it is the day itself and not the day in UTC. */
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', opts);
}

/** Milliseconds until that day, for "expires soon". Infinity when unknown, so
 *  a missing date is never mistaken for an urgent one. */
export function daysUntil(v: unknown): number {
  const day = acteDay(v);
  if (!day) return Infinity;
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).getTime() - Date.now();
}
